import {Injectable} from '@angular/core';
import {addDoc, collection, Firestore, getDocs, onSnapshot, QuerySnapshot} from '@angular/fire/firestore';
import {IFirebaseQuery} from '../../shared-models/firebase/firebase-queries';
import {from, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {generateFirebaseCollectionReference} from '../../shared-utils/firestore/firestore.utils';
import {IFromAppQueueItem, IMessage} from '../../../features/core/models';

@Injectable({
  providedIn: 'root',
})
export class CollectionOperationalMessagesServiceService {

  constructor(private firestore: Firestore) {
  }

  getMessagesUsingQuery<DataType>(path: string, firebaseQueries: IFirebaseQuery[]): Observable<IMessage<DataType>[]> {
    const collectionRef = collection(this.firestore, path);
    const queryRef = generateFirebaseCollectionReference(firebaseQueries, collectionRef);

    return new Observable((observer) => {
      onSnapshot(queryRef, (snapshot) => {
        const messages: IMessage<DataType>[] = [];
        snapshot.forEach((doc) => {
          const docMessage = doc.data() as IMessage<DataType>;
          messages.push(docMessage);
        });
        observer.next(messages);
      }, (error) => {
        observer.error(error);
      });
    });
  }

  /**
   * One-shot read of a from_app collection. Returns the event type and document id.
   * Does not open a listener. An empty queue costs one read; each waiting message costs one read.
   */
  getAllFromAppMessages(path: string): Observable<IFromAppQueueItem[]> {
    const collectionRef = collection(this.firestore, path);
    return from(getDocs(collectionRef)).pipe(
      map((snapshot: QuerySnapshot): IFromAppQueueItem[] => {
        const items: IQueuedFromAppDocument[] = snapshot.docs.map((documentSnapshot): IQueuedFromAppDocument => {
          const data = documentSnapshot.data() as IFromAppDocumentData;
          const type = String(data.type || data.payload?.event || 'Unknown');
          return {
            id: documentSnapshot.id,
            sortMillis: fromAppTimestampMillis(data.timestamp),
            type,
          };
        });
        items.sort((left: IQueuedFromAppDocument, right: IQueuedFromAppDocument): number => right.sortMillis - left.sortMillis);
        return items.map((item: IQueuedFromAppDocument): IFromAppQueueItem => ({id: item.id, type: item.type}));
      }),
    );
  }

  addDocumentToCollection<DataType>(path: string, data: DataType): Observable<void> {
    return from(
      new Promise<void>((resolve, reject) => {
        const collectionRef = collection(this.firestore, path);
        addDoc(collectionRef, data)
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      }),
    );
  }


}

interface IFromAppDocumentData {
  payload?: {event?: string};
  timestamp?: unknown;
  type?: string;
}

interface ITimestampLike {
  seconds?: number;
  toMillis?(): number;
}

interface IQueuedFromAppDocument {
  id: string;
  sortMillis: number;
  type: string;
}

/**
 * Converts a Firestore timestamp, Date, or seconds object into milliseconds for queue ordering.
 */
function fromAppTimestampMillis(value: unknown): number {
  if (!value) {
    return 0;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  const withMillis = value as ITimestampLike;
  if (typeof withMillis.toMillis === 'function') {
    return withMillis.toMillis();
  }
  if (typeof withMillis.seconds === 'number') {
    return withMillis.seconds * 1000;
  }
  return 0;
}
